import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Clock, Bell, Tag, Calendar, Briefcase, Users, Shield, Blocks, Landmark, Radar, Building2, UserPlus,
  Plus, X, Check, ChevronRight, ArrowLeft, ArrowRight, Lock, Pencil,
  type LucideIcon,
} from 'lucide-react';
import { toast } from '../store/toast.js';
import { MODULE_KEYS, type ModuleKey, type Permission } from '@pointage/shared';
import {
  useMe, useSettings, useUpdateSettings, useUsers,
  useDecisionTypes, useCreateDecisionType, useUpdateDecisionType, useDeleteDecisionType,
  useEntities, useCompanies, useHolidaysList, useCreateHoliday, useUpdateHoliday, useDeleteHoliday,
  usePostes, useCreatePoste, useUpdatePoste, useDeletePoste,
  type SettingsMap, type DecisionTypeDTO, type HolidayDTO, type PosteDTO,
} from '../api/hooks.js';
import { AccessCard } from '../components/settings/AccessCard.js';
import { CompaniesCard } from '../components/settings/CompaniesCard.js';
import { ScopePicker, scopeBadges } from '../components/settings/ScopePicker.js';
import { ModulesCard } from '../components/settings/ModulesCard.js';
import { PaieCard } from '../components/settings/PaieCard.js';
import { UsageCard } from '../components/settings/UsageCard.js';
import { RecruitersCard } from '../components/settings/RecruitersCard.js';
import '../styles/parametres.css';
import { useUnsavedGuard } from '../lib/unsaved.js';

// ─── topic metadata (proto SECTIONS) ─────────────────────────────
// `perm`, when set, hides the rubrique from anyone who doesn't hold it —
// on the hub, in the rail, and on direct navigation to its URL.
interface TopicDef { id: string; sec: string; Icon: LucideIcon; title: string; short: string; sub: string; perm?: Permission; }
const SECTIONS: TopicDef[] = [
  { id: 'hours', sec: 'var(--shf)', Icon: Clock, title: 'Heures & seuils de détection', short: 'Heures & seuils',
    sub: 'Heures attendues, arrivée prévue et seuils utilisés par les scans quotidien et hebdomadaire.' },
  { id: 'notif', sec: 'var(--oct)', Icon: Bell, title: 'Notifications automatiques', short: 'Notifications',
    sub: 'Emails déclenchés par les événements du flux Validation → Grille → Congés.' },
  { id: 'types', sec: 'var(--sgn)', Icon: Tag, title: 'Types de décision', short: 'Types de décision',
    sub: 'Catégories utilisées dans la Saisie quotidienne — 7 intégrées, plus vos types personnalisés.' },
  { id: 'hol', sec: 'var(--sga)', Icon: Calendar, title: 'Jours fériés', short: 'Jours fériés',
    sub: 'Jours non travaillés dans la Saisie (comptés sans absence). Portée par entité possible.' },
  { id: 'postes', sec: 'var(--oct)', Icon: Briefcase, title: 'Postes', short: 'Postes',
    sub: 'Catalogue d’intitulés référencé par l’historique et les fiches employés.' },
  { id: 'users', sec: 'var(--shf)', Icon: Users, title: 'Accès & utilisateurs', short: 'Accès',
    sub: 'Comptes, rôles (Boss · Admin · Manager · Assistante), permissions fines et portée par entité.' },
  { id: 'recruteurs', sec: 'var(--oct)', Icon: UserPlus, title: 'Managers recruteurs', short: 'Recruteurs',
    perm: 'users.manage',
    sub: 'Accès limité au module Recrutement : chaque manager recruteur ne voit que les postes que vous lui confiez.' },
  { id: 'modules', sec: 'var(--sgn)', Icon: Blocks, title: 'Modules & visibilité', short: 'Modules',
    sub: 'Activer ou masquer des pans entiers de l’application : paie, évaluations, contrats, documents, espace employé.' },
  { id: 'societes', sec: 'var(--sgn)', Icon: Building2, title: 'Sociétés & mentions légales', short: 'Sociétés',
    sub: 'Logo, raison sociale, adresse, NIF/NIS/AI/CNAS et RIB de chaque entité — repris sur tous les documents générés.' },
  { id: 'paie', sec: 'var(--sga)', Icon: Landmark, title: 'Paie — comptes donneurs d’ordre', short: 'Paie',
    sub: 'Un compte bancaire et un SWIFT par société payeuse — SHIFTIN, Octera et SHIFT GROUP (Alger + Annaba) — estampillés sur leur fichier de virement.' },
  { id: 'usage', sec: 'var(--shf)', Icon: Radar, title: 'Connexions & usage', short: 'Connexions',
    perm: 'usage.view',
    sub: 'Qui se connecte, depuis quel appareil et combien de temps l’app est réellement utilisée. Réservé aux Boss.' },
  { id: 'domain', sec: 'var(--oct)', Icon: Shield, title: 'Domaine autorisé', short: 'Domaine',
    sub: 'Authentification Google Workspace — restreint la connexion à un seul domaine.' },
];
const SEC = (id: string | undefined) => SECTIONS.find((s) => s.id === id);
// The rubriques this account may see. Mirrors the server: the usage
// routes are behind `usage.view`, so hiding the card is cosmetic — a
// direct call still 403s.
const visibleSections = (perms: readonly Permission[]) =>
  SECTIONS.filter((s) => !s.perm || perms.includes(s.perm));
// Exported for the topbar breadcrumb (Header.tsx).
export const SETTINGS_TOPIC_TITLES: Record<string, string> =
  Object.fromEntries(SECTIONS.map((s) => [s.id, s.title]));

// ─── helpers ─────────────────────────────────────────────────────
const pad = (n: number) => String(n).padStart(2, '0');
const fmtMin = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const toMin = (s: string) => { const [h, m] = s.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const PALETTE = ['#7037ef', '#0bb5ab', '#ff5e11', '#d24df3', '#0bb89a', '#f0a500'];

// ─── save-toast (« Enregistré · tracé dans le journal ») ─────────
const PingCtx = createContext<() => void>(() => {});
const useSavePing = () => useContext(PingCtx);

// ═══════════════════════════════════════════════════════════════
export function SettingsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const perms = (me.data?.permissions ?? []) as Permission[];
  const sections = visibleSections(perms);
  // An unknown topic — or one this account can't see — falls back to
  // the hub rather than rendering an empty card.
  const topic = sections.some((s) => s.id === params.topic) ? params.topic! : null;

  const [toastOn, setToastOn] = useState(false);
  const tRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const ping = useCallback(() => {
    setToastOn(true);
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => setToastOn(false), 1900);
  }, []);
  useEffect(() => () => clearTimeout(tRef.current), []);

  // Scroll to top on view change (proto route() behaviour).
  useEffect(() => { window.scrollTo(0, 0); }, [params.topic]);

  return (
    <div className="pset">
      <PingCtx.Provider value={ping}>
        {topic
          ? <Detail topic={topic} sections={sections} navigate={navigate} />
          : <Hub sections={sections} navigate={navigate} />}
      </PingCtx.Provider>
      <div className={'save-toast' + (toastOn ? ' show' : '')} role="status" aria-live="polite">
        <Check aria-hidden="true" />
        <span>Enregistré <em>· tracé dans le journal d’activité</em></span>
      </div>
    </div>
  );
}

// ─── HUB ─────────────────────────────────────────────────────────
function Hub({ sections, navigate }: { sections: TopicDef[]; navigate: ReturnType<typeof useNavigate> }) {
  const settings = useSettings();
  const types = useDecisionTypes();
  const holidays = useHolidaysList({});
  const postes = usePostes();
  const users = useUsers();
  const companies = useCompanies();

  const map: SettingsMap = settings.data ?? {};
  const arr = fmtMin(toMin(String(map.expectedStart ?? '08:00')));
  const late = fmtMin(toMin(String(map.lateThreshold ?? '10:00')));
  const dayH = Number(map.workHours ?? 8);
  const notifOn = ['notifyDayValidated', 'notifyMonthlyStage', 'notifyLeaveRequest', 'notifyContractReminder', 'notifyEvalReminder', 'notifyWeeklySummary']
    .filter((k) => map[k] !== false).length;
  const customCount = (types.data ?? []).filter((t) => !t.builtin).length;
  // Une portée multi-entités = plusieurs lignes pour un même jour /
  // intitulé : on compte ce que l'écran affiche, pas les lignes.
  const holCount = new Set((holidays.data ?? []).map((h) => `${h.date}|${h.name}`)).size;
  const posteCount = new Set((postes.data ?? []).filter((p) => p.active).map((p) => p.label)).size;
  const userCount = (users.data ?? []).filter((u) => u.role !== 'EMPLOYEE' && u.role !== 'RECRUTEUR').length;
  const recruiterCount = (users.data ?? []).filter((u) => u.role === 'RECRUTEUR' && u.active).length;
  const modulesOn = MODULE_KEYS.filter((k) => ((map.modules ?? {}) as Record<string, unknown>)[k] !== false).length;
  const cos = companies.data ?? [];
  const companyCount = cos.length;
  const logoCount = cos.filter((c) => c.logo).length;
  // « Identité complète » = les cinq mentions légales du pied de page.
  const identCount = cos.filter((c) => c.nif && c.nis && c.ai && c.cnas && c.rib).length;

  const chips: Record<string, React.ReactNode[]> = {
    hours: [
      <span className="hub-chip" key="d"><span className="cdot" />{String(dayH).replace('.', ',')} h / jour</span>,
      <span className="hub-chip mono" key="a">arrivée {arr}</span>,
      <span className="hub-chip mono" key="l">retard {late}</span>,
    ],
    notif: [
      <span className="hub-chip on" key="n"><span className="cdot" />{notifOn} / {NOTIFS.length} actives</span>,
      <span className="hub-chip" key="p">Phases A · B · C</span>,
    ],
    types: [
      <span className="hub-chip" key="b"><span className="cdot" />7 intégrés</span>,
      <span className="hub-chip" key="c">{customCount} personnalisé{customCount > 1 ? 's' : ''}</span>,
    ],
    hol: [
      <span className="hub-chip" key="h"><span className="cdot" />{holCount} jours fériés</span>,
      <span className="hub-chip" key="y">année 2026</span>,
    ],
    postes: [
      <span className="hub-chip" key="p"><span className="cdot" />{posteCount} au catalogue</span>,
    ],
    users: [
      <span className="hub-chip" key="u"><span className="cdot" />{userCount} comptes</span>,
      <span className="hub-chip" key="r">Boss · Admin · Manager · Assistante</span>,
    ],
    recruteurs: [
      <span className="hub-chip" key="r"><span className="cdot" />{recruiterCount} manager{recruiterCount > 1 ? 's' : ''} recruteur{recruiterCount > 1 ? 's' : ''}</span>,
      <span className="hub-chip" key="s">module Recrutement seul</span>,
    ],
    modules: [
      <span className="hub-chip on" key="m"><span className="cdot" />{modulesOn} / {MODULE_KEYS.length} actifs</span>,
      <span className="hub-chip" key="e">espace employé inclus</span>,
    ],
    societes: [
      <span className="hub-chip" key="c"><span className="cdot" />{companyCount} société{companyCount > 1 ? 's' : ''}</span>,
      <span className="hub-chip" key="l">{logoCount} logo{logoCount > 1 ? 's' : ''} · {identCount} identité{identCount > 1 ? 's' : ''} complète{identCount > 1 ? 's' : ''}</span>,
    ],
    paie: [
      <span className="hub-chip mono" key="a"><span className="cdot" />···{String(map.payrollOrderAccount ?? '02700721000061000133').slice(-6)}</span>,
      <span className="hub-chip mono" key="s">{String(map.payrollOrderSwift ?? 'BNPADZALXXX')}</span>,
    ],
    domain: [
      <span className="hub-chip mono" key="d"><span className="cdot" />@shiftin.co</span>,
      <span className="hub-chip on" key="v"><span className="cdot" />vérifié</span>,
    ],
    usage: [
      <span className="hub-chip" key="t"><span className="cdot" />temps actif &amp; connexions</span>,
      <span className="hub-chip" key="r">12 mois d’historique</span>,
    ],
  };

  return (
    <div className="view">
      <div className="hero anim">
        <div className="hero-mesh" />
        <div className="hero-eyebrow">Configuration · accès Boss et Administrateur</div>
        <h1 className="hero-title">Paramètres</h1>
        <p className="hero-sub">
          Choisissez une rubrique pour ouvrir sa page dédiée. Chaque modification est enregistrée
          immédiatement et tracée dans le journal d’activité.
        </p>
      </div>
      <div className="hub-grid">
        {sections.map((s) => (
          <button key={s.id} className="hubcard" style={{ '--sec': s.sec } as React.CSSProperties}
            onClick={() => navigate(`/settings/${s.id}`)} data-testid={`hubcard-${s.id}`}>
            <div className="hubcard-ico"><s.Icon /></div>
            <div className="hubcard-main">
              <div className="hubcard-row">
                <div className="hubcard-title">{s.title}</div>
                <span className="hubcard-arrow"><ArrowRight /></span>
              </div>
              <div className="hubcard-sub">{s.sub}</div>
              <div className="hubcard-foot">{chips[s.id]}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── DETAIL ──────────────────────────────────────────────────────
function Detail({ topic, sections, navigate }: {
  topic: string; sections: TopicDef[]; navigate: ReturnType<typeof useNavigate>;
}) {
  const s = SEC(topic)!;
  const i = sections.indexOf(s);
  const prev = sections[(i - 1 + sections.length) % sections.length]!;
  const next = sections[(i + 1) % sections.length]!;

  return (
    <div className="view">
      <div className="detail-bar">
        <button className="backlink" onClick={() => navigate('/settings')} data-testid="set-back">
          <ArrowLeft /> Tous les paramètres
        </button>
        <span className="detail-crumb">Paramètres&nbsp; / &nbsp;<b>{s.title}</b></span>
        <div className="detail-nav">
          <button className="dnav-btn" onClick={() => navigate(`/settings/${prev.id}`)}>
            <ArrowLeft /> {prev.title.split(' ')[0]}
          </button>
          <button className="dnav-btn" onClick={() => navigate(`/settings/${next.id}`)}>
            {next.title.split(' ')[0]} <ArrowRight />
          </button>
        </div>
      </div>

      <div className="detail-layout">
        <nav className="topic-rail">
          <div className="topic-label">Rubriques</div>
          {sections.map((o) => (
            <button key={o.id} className={'topic-item' + (o.id === topic ? ' active' : '')}
              style={{ '--ti': o.sec } as React.CSSProperties}
              onClick={() => navigate(`/settings/${o.id}`)}>
              <span className="ti-ico"><o.Icon /></span>{o.short}
            </button>
          ))}
        </nav>
        <TopicCard topic={topic} />
      </div>
    </div>
  );
}

function ScardHead({ s, tag }: { s: TopicDef; tag: React.ReactNode }) {
  return (
    <div className="scard-head">
      <div className="scard-ico"><s.Icon /></div>
      <div className="scard-titles">
        <div className="scard-title">{s.title}</div>
        <div className="scard-sub">{s.sub}</div>
      </div>
      {tag}
    </div>
  );
}

function TopicCard({ topic }: { topic: string }) {
  switch (topic) {
    case 'hours': return <HoursCard />;
    case 'notif': return <NotifCard />;
    case 'types': return <TypesCard />;
    case 'hol': return <HolCard />;
    case 'postes': return <PostesCard />;
    case 'users': return <UsersSection />;
    case 'recruteurs': return <RecruitersSection />;
    case 'modules': return <ModulesSection />;
    case 'societes': return <CompaniesSection />;
    case 'paie': return <PaieSection />;
    case 'usage': return <UsageSection />;
    case 'domain': return <DomainCard />;
    default: return null;
  }
}

// ═══ HOURS ═══════════════════════════════════════════════════════
const SPAN0 = 420, SPAN1 = 1140; // 07:00 → 19:00
const xPct = (m: number) => ((m - SPAN0) / (SPAN1 - SPAN0)) * 100;

function HoursCard() {
  const s = SEC('hours')!;
  const settings = useSettings();
  if (!settings.data) {
    return (
      <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties}>
        <ScardHead s={s} tag={<span className="scard-tag">scans quotidien · hebdo</span>} />
        <div className="scard-body"><p className="srow-help" style={{ padding: 16 }}>Chargement…</p></div>
      </section>
    );
  }
  return <HoursEditor s={s} map={settings.data} />;
}

function HoursEditor({ s, map }: { s: TopicDef; map: SettingsMap }) {
  const update = useUpdateSettings();
  const ping = useSavePing();
  const [arr, setArr] = useState(toMin(String(map.expectedStart ?? '08:00')));
  const [late, setLate] = useState(toMin(String(map.lateThreshold ?? '10:00')));
  const [dayH, setDayH] = useState(Number(map.workHours ?? 8));
  const [week, setWeek] = useState(Number(map.weeklyHours ?? 40));
  const [tol, setTol] = useState(Number(map.hoursToleranceMinutes ?? 15));
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState<'arr' | 'late' | null>(null);

  const save = (key: string, value: unknown) => {
    update.mutate({ [key]: value }, {
      onSuccess: ping,
      onError: () => toast.danger('Échec de l’enregistrement.'),
    });
  };

  const commitArr = (m: number) => { const v = Math.max(SPAN0, Math.min(m, late - 30)); setArr(v); save('expectedStart', fmtMin(v)); };
  const commitLate = (m: number) => { const v = Math.max(arr + 30, Math.min(m, SPAN1 - 30)); setLate(v); save('lateThreshold', fmtMin(v)); };
  const commitDay = (v: number) => { const c = clamp(v, 4, 12); setDayH(c); save('workHours', c); };
  const commitWeek = (v: number) => { const c = clamp(v, 20, 60); setWeek(c); save('weeklyHours', c); };
  const commitTol = (v: number) => { const c = clamp(v, 0, 60); setTol(c); save('hoursToleranceMinutes', c); };

  const startDrag = (key: 'arr' | 'late') => (e: React.PointerEvent) => {
    e.preventDefault();
    setDragging(key);
    const rect = trackRef.current!.getBoundingClientRect();
    let last = key === 'arr' ? arr : late;
    const move = (ev: PointerEvent) => {
      let m = SPAN0 + ((ev.clientX - rect.left) / rect.width) * (SPAN1 - SPAN0);
      m = Math.round(m / 5) * 5;
      if (key === 'arr') { last = Math.max(SPAN0, Math.min(m, late - 30)); setArr(last); }
      else { last = Math.max(arr + 30, Math.min(m, SPAN1 - 30)); setLate(last); }
    };
    const up = () => {
      setDragging(null);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      save(key === 'arr' ? 'expectedStart' : 'lateThreshold', fmtMin(last));
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const bandL = xPct(arr), bandW = xPct(arr + dayH * 60) - xPct(arr);
  const ticks = [];
  for (let h = 7; h <= 19; h++) ticks.push(h);

  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-hours">
      <ScardHead s={s} tag={<span className="scard-tag">scans quotidien · hebdo</span>} />
      <div className="scard-body">
        <div className="tl-wrap">
          <div className="tl-legend">
            <div className="tl-leg"><span className="swatch" style={{ background: 'color-mix(in srgb,var(--oct) 40%,transparent)' }} />Journée attendue <b>{Math.floor(dayH)} h {pad(Math.round((dayH % 1) * 60))}</b></div>
            <div className="tl-leg"><span className="swatch" style={{ background: 'var(--shf)' }} />Arrivée prévue <b>{fmtMin(arr)}</b></div>
            <div className="tl-leg"><span className="swatch" style={{ background: 'var(--warn)' }} />Seuil de retard <b>{fmtMin(late)}</b></div>
          </div>
          <div className="track" ref={trackRef}>
            <div className="track-grid">
              {ticks.map((h) => (
                <span key={h}>
                  <span className="tick" style={{ left: xPct(h * 60) + '%' }} />
                  <span className="tick-lbl" style={{ left: xPct(h * 60) + '%' }}>{pad(h)}h</span>
                </span>
              ))}
            </div>
            <div className="band-late" style={{ left: xPct(late) + '%' }} />
            <div className="band-expected" style={{ left: bandL + '%', width: bandW + '%' }} />
            <div className={'handle arr' + (dragging === 'arr' ? ' drag' : '')} style={{ left: xPct(arr) + '%' }} onPointerDown={startDrag('arr')}>
              <div className="handle-flag"><small>Arrivée</small><span>{fmtMin(arr)}</span></div>
              <div className="handle-grip" />
            </div>
            <div className={'handle late' + (dragging === 'late' ? ' drag' : '')} style={{ left: xPct(late) + '%' }} onPointerDown={startDrag('late')}>
              <div className="handle-flag"><small>Seuil retard</small><span>{fmtMin(late)}</span></div>
              <div className="handle-grip" />
            </div>
          </div>
        </div>

        <div className="set-rows">
          <SRow label="Heures attendues par jour" help="Durée standard d’une journée travaillée."
            control={<><Stepper value={dayH} min={4} max={12} inc={0.5} decimal testid="set-workHours" onCommit={commitDay} /><span className="srow-suffix">heures</span></>} />
          <SRow label="Heures attendues par semaine" help="Volume hebdomadaire de référence."
            control={<><Stepper value={week} min={20} max={60} inc={1} testid="set-weeklyHours" onCommit={commitWeek} /><span className="srow-suffix">heures</span></>} />
          <SRow label="Heure d’arrivée prévue" help="Référence pour le calcul des retards (HH:MM)."
            control={<div className="time-input"><input type="time" className="time-pick" value={fmtMin(arr)} data-testid="set-expectedStart" aria-label="Heure d’arrivée prévue" onChange={(e) => commitArr(toMin(e.target.value))} /></div>} />
          <SRow label="Seuil de retard" help="Un retard est détecté au-delà de cette heure d’entrée (HH:MM)."
            control={<div className="time-input"><input type="time" className="time-pick" value={fmtMin(late)} data-testid="set-lateThreshold" aria-label="Seuil de retard" onChange={(e) => commitLate(toMin(e.target.value))} /></div>} />
          <SRow label="Tolérance heures" help="Marge en minutes avant de signaler une journée courte."
            control={<><Stepper value={tol} min={0} max={60} inc={5} testid="set-tolerance" onCommit={commitTol} /><span className="srow-suffix">min</span></>} />
        </div>
      </div>
    </section>
  );
}

function clamp(v: number, min: number, max: number) { return Math.max(min, Math.min(max, v)); }

function SRow({ label, help, control }: { label: string; help: string; control: React.ReactNode }) {
  return (
    <div className="srow">
      <div><div className="srow-label">{label}</div><div className="srow-help">{help}</div></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{control}</div>
    </div>
  );
}

function Stepper({ value, min, max, inc, decimal, testid, onCommit }: {
  value: number; min: number; max: number; inc: number; decimal?: boolean; testid: string; onCommit: (v: number) => void;
}) {
  const fmt = (v: number) => (decimal ? String(v).replace('.', ',') : String(v));
  const [text, setText] = useState(fmt(value));
  useEffect(() => { setText(fmt(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = (raw: string) => {
    let n = parseFloat(raw.replace(',', '.'));
    if (Number.isNaN(n)) n = value;
    n = clamp(Math.round(n / inc) * inc, min, max);
    onCommit(+n.toFixed(1));
  };
  return (
    <div className="stepper">
      <button type="button" aria-label="Diminuer" onClick={() => onCommit(clamp(+(value - inc).toFixed(1), min, max))}>−</button>
      <input value={text} inputMode={decimal ? 'decimal' : 'numeric'} data-testid={testid} aria-label={testid}
        onChange={(e) => setText(e.target.value)} onBlur={() => commit(text)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(text); }} />
      <button type="button" aria-label="Augmenter" onClick={() => onCommit(clamp(+(value + inc).toFixed(1), min, max))}>+</button>
    </div>
  );
}

// ═══ NOTIFICATIONS ═══════════════════════════════════════════════
const NOTIFS = [
  { ph: 'A', key: 'notifyDayValidated', name: 'Validation', title: 'Confirmation de validation quotidienne',
    desc: 'Un seul email par journée, toutes sociétés : « à revoir » quand la saisie est finie partout, puis le récapitulatif quand tout est validé.',
    subj: 'Pointage validé · 4 sociétés · mar. 9 juin 2026', body: '44 salariés — 0 absence sans préavis.' },
  { ph: 'B', key: 'notifyMonthlyStage', name: 'Grille', title: 'Notification de soumission grille mensuelle',
    desc: 'Email au valideur suivant à chaque étape de la grille (Phase B).',
    subj: 'Grille mai 2026 — étape 2/3', body: 'La grille attend votre validation.' },
  { ph: 'C', key: 'notifyLeaveRequest', name: 'Congés', title: 'Notification de demande de congé',
    desc: 'Email à l’employé quand une demande de congé est approuvée ou refusée (Phase C).',
    subj: 'Votre demande de congé', body: 'Congé du 14 au 18 juin : approuvé.' },
  { ph: 'K', key: 'notifyContractReminder', name: 'Contrats', title: 'Rappel d’échéance de contrat',
    desc: 'Email à la direction quand un jalon d’essai ou de fin de CDD est relancé depuis la page Période d’essai & CDD.',
    subj: 'Rappel contrat · Décision à préparer (J-30)', body: 'La période d’essai arrive à terme — merci de statuer.' },
  { ph: 'E', key: 'notifyEvalReminder', name: 'Évaluations', title: 'Relance d’auto-évaluation annuelle',
    desc: 'Email à l’employé quand sa campagne d’auto-évaluation est relancée depuis la page Évaluations.',
    subj: 'Rappel — votre auto-évaluation annuelle', body: 'La date limite approche — comptez 30 minutes pour la compléter.' },
  { ph: 'A', key: 'notifyWeeklySummary', name: 'Récap hebdo', title: 'Récapitulatif hebdomadaire à la direction',
    desc: 'Email aux Boss le dimanche matin sur la semaine dim.–jeu. qui vient de se terminer : heures non atteintes, arrivées tardives répétées, journées de 10 h ou plus, semaines de plus de 50 h.',
    subj: 'Récap hebdo · dim. 30 août → jeu. 3 septembre 2026', body: '2 sous les heures · 1 retards répétés · 3 journées longues.' },
] as const;

function NotifCard() {
  const s = SEC('notif')!;
  const settings = useSettings();
  const update = useUpdateSettings();
  const ping = useSavePing();
  const map: SettingsMap = settings.data ?? {};
  const on = (k: string) => map[k] !== false;
  const count = NOTIFS.filter((n) => on(n.key)).length;
  const [open, setOpen] = useState<Record<string, boolean>>({ A: true, B: true, C: true });

  const toggle = (key: string) => {
    update.mutate({ [key]: !on(key) }, { onSuccess: ping, onError: () => toast.danger('Échec de l’enregistrement.') });
  };

  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-notif">
      <ScardHead s={s} tag={<span className="scard-tag">{count} actives</span>} />
      <div className="scard-body">
        <div className="phase-pipe">
          {NOTIFS.map((n) => (
            <div key={n.ph} className={'phase' + (on(n.key) ? ' on' : '')}>
              <div className="phase-line" /><div className="phase-node">{n.ph}</div><div className="phase-name">{n.name}</div>
            </div>
          ))}
        </div>
        <div>
          {NOTIFS.map((n) => (
            <div key={n.ph} className={'ntf' + (open[n.ph] ? ' open' : '')}>
              <div className="ntf-row" onClick={(e) => { if ((e.target as HTMLElement).closest('.toggle')) return; setOpen((o) => ({ ...o, [n.ph]: !o[n.ph] })); }}>
                <span className="ntf-badge">Phase {n.ph}</span>
                <div><div className="ntf-title">{n.title}</div><div className="ntf-desc">{n.desc}</div></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span className="ntf-chevron"><ChevronRight /></span>
                  <button className={'toggle' + (on(n.key) ? ' on' : '')} role="switch" aria-checked={on(n.key)}
                    aria-label={n.title} data-testid={`set-${n.key}`} onClick={() => toggle(n.key)} />
                </div>
              </div>
              <div className="ntf-preview">
                <div className="mailcard">
                  <div className="mail-head"><span className="mail-dot" /><span className="mail-from">pointage@shiftin.co</span><span className="mail-to">→ destinataire</span></div>
                  <div className="mail-body"><div className="mail-subj">{n.subj}</div>{n.body}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══ TYPES DE DÉCISION ═══════════════════════════════════════════
const DT_COLOR: Record<string, string> = {
  PRESENT: 'var(--ok)', TT: 'var(--shf)', CONGE: 'var(--sgn)', MALADIE: 'var(--info)',
  RDV_PERSO: 'var(--sga)', RECUP: 'var(--accent-2)', AUTRE: 'var(--ink-4)',
};
const codeColor = (code: string) => {
  let h = 0; for (const c of code) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

function TypesCard() {
  const s = SEC('types')!;
  const types = useDecisionTypes();
  const create = useCreateDecisionType();
  const update = useUpdateDecisionType();
  const remove = useDeleteDecisionType();
  const ping = useSavePing();
  const [label, setLabel] = useState('');
  const [color, setColor] = useState(PALETTE[0]);
  const [fresh, setFresh] = useState<string | null>(null);
  // Non-null = the addbar is editing that type instead of adding one.
  const [editing, setEditing] = useState<DecisionTypeDTO | null>(null);

  const builtins = (types.data ?? []).filter((t) => t.builtin);
  const customs = (types.data ?? []).filter((t) => !t.builtin);

  const reset = () => { setEditing(null); setLabel(''); };
  const edit = (t: DecisionTypeDTO) => { setEditing(t); setLabel(t.label); };

  const submit = async (): Promise<boolean> => {
    const v = label.trim();
    if (!v) return false;
    try {
      if (editing?.id) {
        await update.mutateAsync({ id: editing.id, label: v });
        setFresh(editing.code);
      } else {
        const res = await create.mutateAsync({ label: v });
        setFresh(res.code);
      }
      reset(); ping();
      return true;
    } catch (e) {
      const body = (e as Error & { body?: { error?: string } }).body;
      toast.danger(body?.error === 'duplicate_code' ? 'Ce type existe déjà.'
        : editing ? 'Échec de l’enregistrement.' : 'Échec de l’ajout.');
      return false;
    }
  };
  useUnsavedGuard({
    id: 'settings-decision-type',
    dirty: editing ? label.trim() !== editing.label : label.trim() !== '',
    label: 'Paramètres · types de décision',
    summary: editing ? `« ${editing.label} »` : `nouveau type « ${label.trim()} »`,
    save: label.trim() ? async () => { if (!(await submit())) throw new Error('save_failed'); } : undefined,
    discard: reset,
  });
  const del = async (t: DecisionTypeDTO) => {
    if (!t.id) return;
    if (editing?.id === t.id) reset();
    try { await remove.mutateAsync(t.id); ping(); }
    catch { toast.danger('Échec de la suppression.'); }
  };

  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-decision-types">
      <ScardHead s={s} tag={<span className="scard-tag">{customs.length ? `7 + ${customs.length}` : '7 intégrés'}</span>} />
      <div className="scard-body">
        <div className="set-sub-label">Types intégrés · verrouillés</div>
        <div className="pillgrid">
          {builtins.map((t) => (
            <span key={t.code} className="dpill" style={{ '--pc': DT_COLOR[t.code] ?? 'var(--ink-4)' } as React.CSSProperties} data-testid={`dt-builtin-${t.code}`}>
              <span className="pdot" />{t.label}<span className="lock"><Lock /></span>
            </span>
          ))}
        </div>
        <div className="set-sub-label" style={{ paddingTop: 10 }}>Types personnalisés</div>
        <div className="pillgrid">
          {customs.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--ink-4)', padding: '6px 8px' }}>Aucun type personnalisé pour l’instant.</span>}
          {customs.map((t) => (
            <span key={t.code}
              className={'dpill custom' + (fresh === t.code ? ' fresh' : '') + (editing?.code === t.code ? ' editing' : '')}
              style={{ '--pc': codeColor(t.code) } as React.CSSProperties} data-testid={`dt-custom-${t.code}`}>
              <span className="pdot" />{t.label}
              <button className="ed" onClick={() => edit(t)} data-testid={`dt-edit-${t.code}`} aria-label={`Modifier ${t.label}`}><Pencil /></button>
              <button className="rm" onClick={() => del(t)} data-testid={`dt-delete-${t.code}`} aria-label={`Retirer ${t.label}`}><X /></button>
            </span>
          ))}
        </div>
        <div className={'addbar' + (editing ? ' editing' : '')}>
          <span className="addbar-label">
            {editing
              ? <>Modifier le type — <b>le code <code>{editing.code}</code> reste inchangé sur les pointages déjà saisis</b></>
              : <>Ajouter un type — <b>ex. « Formation », « Mission externe »</b></>}
          </span>
          {!editing && (
            <div className="color-picker">
              {PALETTE.map((c) => (
                <button key={c} className={'swatch-btn' + (color === c ? ' sel' : '')} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Couleur ${c}`} />
              ))}
            </div>
          )}
          <input className="finput" value={label} onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') reset(); }}
            placeholder="Nom du type" maxLength={60}
            data-testid="dt-new-label" aria-label={editing ? 'Nom du type' : 'Nouveau type de décision'} />
          {editing && <button className="btn" onClick={reset} data-testid="dt-cancel">Annuler</button>}
          <button className="btn btn-primary" onClick={submit}
            disabled={create.isPending || update.isPending || !label.trim()} data-testid="dt-add">
            {editing ? <><Check /> Enregistrer</> : <><Plus /> Ajouter</>}
          </button>
        </div>
      </div>
    </section>
  );
}

// ═══ JOURS FÉRIÉS ════════════════════════════════════════════════
// Un jour férié posé sur plusieurs entités = une ligne par entité ;
// on les regroupe ici par (date, libellé) pour n'afficher qu'une carte
// portant toutes ses portées.
interface HolGroup { key: string; date: string; name: string; ids: string[]; scopes: Array<string | null>; }

function groupHolidays(rows: HolidayDTO[]): HolGroup[] {
  const map = new Map<string, HolGroup>();
  for (const h of rows) {
    const key = `${h.date}|${h.name}`;
    const g = map.get(key) ?? { key, date: h.date, name: h.name, ids: [], scopes: [] };
    g.ids.push(h.id);
    g.scopes.push(h.entityId);
    map.set(key, g);
  }
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name));
}

function HolCard() {
  const s = SEC('hol')!;
  const holidays = useHolidaysList({});
  const entities = useEntities();
  const create = useCreateHoliday();
  const update = useUpdateHoliday();
  const remove = useDeleteHoliday();
  const ping = useSavePing();
  const [date, setDate] = useState('2026-12-25');
  const [name, setName] = useState('');
  const [scope, setScope] = useState<string[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  // Non-null = the addbar is editing that holiday instead of adding one.
  const [editing, setEditing] = useState<HolGroup | null>(null);

  const ents = entities.data ?? [];
  const groups = useMemo(() => groupHolidays(holidays.data ?? []), [holidays.data]);
  const counts = useMemo(() => {
    const c = Array(12).fill(0);
    groups.forEach((g) => { const mo = +g.date.split('-')[1] - 1; if (mo >= 0 && mo < 12) c[mo]++; });
    return c;
  }, [groups]);

  const reset = () => { setEditing(null); setName(''); setScope([]); };
  const edit = (g: HolGroup) => {
    setEditing(g); setDate(g.date); setName(g.name);
    setScope(g.scopes.some((x) => x === null) ? [] : (g.scopes.filter(Boolean) as string[]));
  };

  const submit = async (): Promise<boolean> => {
    if (!date || !name.trim()) { toast.danger('Date et libellé requis.'); return false; }
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.ids[0], patch: { date, name: name.trim(), entityIds: scope } });
      } else {
        await create.mutateAsync({ date, name: name.trim(), entityIds: scope });
      }
      setFresh(`${date}|${name.trim()}`); reset(); ping();
      return true;
    } catch (e) {
      const body = (e as Error & { body?: { error?: string } }).body;
      toast.danger(body?.error === 'duplicate_holiday'
        ? 'Ce jour férié existe déjà pour au moins une des entités choisies.'
        : editing ? 'Échec de l’enregistrement.' : 'Échec de l’ajout.');
      return false;
    }
  };
  useUnsavedGuard({
    id: 'settings-holiday',
    dirty: editing ? (date !== editing.date || name.trim() !== editing.name) : name.trim() !== '',
    label: 'Paramètres · jours fériés',
    summary: editing ? `« ${editing.name} »` : `nouveau férié « ${name.trim()} »`,
    save: date && name.trim() ? async () => { if (!(await submit())) throw new Error('save_failed'); } : undefined,
    discard: reset,
  });
  const del = (g: HolGroup) => {
    if (editing?.key === g.key) reset();
    setRemoving(g.key);
    setTimeout(async () => {
      try { await Promise.all(g.ids.map((id) => remove.mutateAsync(id))); ping(); }
      catch { toast.danger('Échec de la suppression.'); }
      setRemoving(null);
    }, 300);
  };

  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-holidays">
      <ScardHead s={s} tag={<span className="scard-tag">{groups.length} jours · 2026</span>} />
      <div className="scard-body">
        <div className="year-ribbon">
          {MONTHS.map((m, i) => (
            <div key={m} className={'ymonth' + (counts[i] ? ' has' : '')}>
              <div className="ymonth-name">{m}</div>
              <div className="ymonth-count">{counts[i] || '·'}</div>
              <div className="pips">{Array.from({ length: counts[i] }).map((_, j) => <span className="pip" key={j} />)}</div>
            </div>
          ))}
        </div>
        <div className="hol-list">
          {groups.map((g) => {
            const [, mo, d] = g.date.split('-');
            return (
              <div key={g.key}
                className={'hol' + (removing === g.key ? ' removing' : '') + (fresh === g.key ? ' fresh' : '') + (editing?.key === g.key ? ' editing' : '')}
                data-testid={`holiday-${g.ids[0]}`}>
                <div className="hol-cal"><div className="hol-cal-m">{MONTHS[+mo - 1]}</div><div className="hol-cal-d">{d}</div></div>
                <div><div className="hol-name">{g.name}</div><div className="hol-date">{g.date}</div></div>
                <span className="scope-badges">
                  {scopeBadges(g.scopes, ents).map((b) => (
                    <span key={b.key} className={`scope-badge ${b.cls}`}>{b.label}</span>
                  ))}
                </span>
                <button className="hol-ed" onClick={() => edit(g)} data-testid={`holiday-edit-${g.ids[0]}`} aria-label={`Modifier ${g.name}`}><Pencil /></button>
                <button className="hol-rm" onClick={() => del(g)} data-testid={`holiday-delete-${g.ids[0]}`} aria-label={`Supprimer ${g.name}`}><X /></button>
              </div>
            );
          })}
          {groups.length === 0 && <div className="srow"><div className="srow-help">Aucun jour férié enregistré.</div></div>}
        </div>
        <div className={'addbar' + (editing ? ' editing' : '')}>
          <span className="addbar-label">
            {editing
              ? <>Modifier le jour férié — <b>date, libellé et portée</b></>
              : <>Ajouter un jour férié — <b>date, libellé et portée</b></>}
          </span>
          <input type="date" className="finput date-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="holiday-new-date" aria-label="Date du jour férié" />
          <input className="finput" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') reset(); }}
            placeholder="Libellé" maxLength={80} data-testid="holiday-new-name" aria-label="Libellé du jour férié" />
          <ScopePicker ents={ents} value={scope} onChange={setScope} testId="holiday-new-entity" label="Portée du jour férié" />
          {editing && <button className="btn" onClick={reset} data-testid="holiday-cancel">Annuler</button>}
          <button className="btn btn-primary" onClick={submit}
            disabled={create.isPending || update.isPending || !date || !name.trim()} data-testid="holiday-add">
            {editing ? <><Check /> Enregistrer</> : <><Plus /> Ajouter</>}
          </button>
        </div>
      </div>
    </section>
  );
}

// ═══ POSTES ══════════════════════════════════════════════════════
const SCOPE_PC: Record<string, string> = { all: 'var(--ink-3)', shf: 'var(--shf)', oct: 'var(--oct)', sga: 'var(--sga)', sgn: 'var(--sgn)' };

// Même regroupement que les jours fériés : un intitulé ouvert à
// plusieurs entités = une ligne de catalogue par entité, une seule
// pastille à l'écran.
interface PosteGroup { label: string; ids: string[]; scopes: Array<string | null>; }

function groupPostes(rows: PosteDTO[]): PosteGroup[] {
  const map = new Map<string, PosteGroup>();
  for (const p of rows) {
    const g = map.get(p.label) ?? { label: p.label, ids: [], scopes: [] };
    g.ids.push(p.id);
    g.scopes.push(p.entityId);
    map.set(p.label, g);
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
}

function PostesCard() {
  const s = SEC('postes')!;
  const postes = usePostes();
  const entities = useEntities();
  const create = useCreatePoste();
  const update = useUpdatePoste();
  const remove = useDeletePoste();
  const ping = useSavePing();
  const [label, setLabel] = useState('');
  const [scope, setScope] = useState<string[]>([]);
  // Non-null = the addbar is editing that poste instead of adding one.
  const [editing, setEditing] = useState<PosteGroup | null>(null);

  const ents = entities.data ?? [];
  const groups = useMemo(() => groupPostes((postes.data ?? []).filter((p) => p.active)), [postes.data]);

  const reset = () => { setEditing(null); setLabel(''); setScope([]); };
  const edit = (g: PosteGroup) => {
    setEditing(g); setLabel(g.label);
    setScope(g.scopes.some((x) => x === null) ? [] : (g.scopes.filter(Boolean) as string[]));
  };

  const submit = async (): Promise<boolean> => {
    if (!label.trim()) { toast.danger('Libellé requis.'); return false; }
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.ids[0], patch: { label: label.trim(), entityIds: scope } });
      } else {
        await create.mutateAsync({ label: label.trim(), entityIds: scope });
      }
      reset(); ping();
      return true;
    } catch (e) {
      const body = (e as Error & { body?: { error?: string } }).body;
      toast.danger(body?.error === 'duplicate_poste'
        ? 'Ce poste est déjà au catalogue pour les entités choisies.'
        : editing ? 'Échec de l’enregistrement.' : 'Échec de l’ajout.');
      return false;
    }
  };
  useUnsavedGuard({
    id: 'settings-poste',
    dirty: editing ? label.trim() !== editing.label : label.trim() !== '',
    label: 'Paramètres · postes',
    summary: editing ? `« ${editing.label} »` : `nouveau poste « ${label.trim()} »`,
    save: label.trim() ? async () => { if (!(await submit())) throw new Error('save_failed'); } : undefined,
    discard: reset,
  });
  const del = async (g: PosteGroup) => {
    if (editing?.label === g.label) reset();
    try { await Promise.all(g.ids.map((id) => remove.mutateAsync(id))); ping(); }
    catch { toast.danger('Échec de la suppression.'); }
  };

  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-postes">
      <ScardHead s={s} tag={<span className="scard-tag">{groups.length} poste{groups.length > 1 ? 's' : ''}</span>} />
      <div className="scard-body">
        {groups.length === 0 ? (
          <div className="empty">
            <div className="empty-ico"><Briefcase /></div>
            <div className="empty-title">Aucun poste au catalogue</div>
            <div className="empty-sub">Ajoutez les intitulés de poste utilisés par vos fiches employés.</div>
          </div>
        ) : (
          <div className="poste-grid">
            {groups.map((g) => {
              const badges = scopeBadges(g.scopes, ents);
              return (
                <span key={g.label}
                  className={'dpill custom' + (editing?.label === g.label ? ' editing' : '')}
                  style={{ '--pc': SCOPE_PC[badges[0]?.cls ?? 'all'] ?? 'var(--ink-3)' } as React.CSSProperties}
                  data-testid={`poste-${g.ids[0]}`}>
                  <span className="pdot" />{g.label}
                  <span className="scope-badges">
                    {badges.map((b) => <span key={b.key} className={`scope-badge ${b.cls}`}>{b.label}</span>)}
                  </span>
                  <button className="ed" onClick={() => edit(g)} data-testid={`poste-edit-${g.ids[0]}`} aria-label={`Modifier ${g.label}`}><Pencil /></button>
                  <button className="rm" onClick={() => del(g)} data-testid={`poste-delete-${g.ids[0]}`} aria-label={`Retirer ${g.label}`}><X /></button>
                </span>
              );
            })}
          </div>
        )}
        <div className={'addbar' + (editing ? ' editing' : '')}>
          <span className="addbar-label">
            {editing
              ? <>Modifier le poste — <b>le renommage suit l’historique des fiches employés</b></>
              : <>Ajouter un poste — <b>ex. « Développeur », « Comptable »</b></>}
          </span>
          <input className="finput" value={label} onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') reset(); }}
            placeholder="Libellé" maxLength={80}
            data-testid="poste-new-label" aria-label="Libellé du poste" />
          <ScopePicker ents={ents} value={scope} onChange={setScope} testId="poste-new-entity" label="Portée du poste" />
          {editing && <button className="btn" onClick={reset} data-testid="poste-cancel">Annuler</button>}
          <button className="btn btn-primary" onClick={submit}
            disabled={create.isPending || update.isPending || !label.trim()} data-testid="poste-add">
            {editing ? <><Check /> Enregistrer</> : <><Plus /> Ajouter</>}
          </button>
        </div>
      </div>
    </section>
  );
}

// ═══ ACCÈS & UTILISATEURS · MODULES · PAIE ═══════════════════════
function RecruitersSection() {
  const s = SEC('recruteurs')!;
  const users = useUsers();
  const ping = useSavePing();
  const n = (users.data ?? []).filter((u) => u.role === 'RECRUTEUR' && u.active).length;
  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-recruteurs">
      <ScardHead s={s} tag={<span className="scard-tag">{n} actif{n > 1 ? 's' : ''}</span>} />
      <RecruitersCard ping={ping} />
    </section>
  );
}

function UsersSection() {
  const s = SEC('users')!;
  const users = useUsers();
  const staff = (users.data ?? []).filter((u) => u.role !== 'EMPLOYEE');
  const ping = useSavePing();
  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-users">
      <ScardHead s={s} tag={<span className="scard-tag">{staff.length} compte{staff.length > 1 ? 's' : ''} staff</span>} />
      <AccessCard ping={ping} />
    </section>
  );
}

function ModulesSection() {
  const s = SEC('modules')!;
  const settings = useSettings();
  const map: SettingsMap = settings.data ?? {};
  const stored = (map.modules ?? {}) as Partial<Record<ModuleKey, unknown>>;
  const onCount = MODULE_KEYS.filter((k) => stored[k] !== false).length;
  const ping = useSavePing();
  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-modules">
      <ScardHead s={s} tag={<span className="scard-tag">{onCount} / {MODULE_KEYS.length} actifs</span>} />
      <ModulesCard ping={ping} />
    </section>
  );
}

function CompaniesSection() {
  const s = SEC('societes')!;
  const ping = useSavePing();
  const companies = useCompanies();
  const n = (companies.data ?? []).length;
  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-societes">
      <ScardHead s={s} tag={<span className="scard-tag">{n} société{n > 1 ? 's' : ''}</span>} />
      <CompaniesCard ping={ping} />
    </section>
  );
}

function PaieSection() {
  const s = SEC('paie')!;
  const ping = useSavePing();
  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-paie">
      <ScardHead s={s} tag={<span className="scard-tag">fichier banque</span>} />
      <PaieCard ping={ping} />
    </section>
  );
}

// ═══ CONNEXIONS & USAGE ══════════════════════════════════════════
function UsageSection() {
  const s = SEC('usage')!;
  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-usage">
      <ScardHead s={s} tag={<span className="scard-tag">lecture seule · Boss</span>} />
      <UsageCard />
    </section>
  );
}

// ═══ DOMAINE ═════════════════════════════════════════════════════
function DomainCard() {
  const s = SEC('domain')!;
  return (
    <section className="scard anim" style={{ '--sec': s.sec } as React.CSSProperties} data-testid="section-domain">
      <ScardHead s={s} tag={<span className="scard-tag">SSO</span>} />
      <div className="scard-body">
        <div className="domain-card">
          <div className="domain-lock"><Lock /></div>
          <div>
            <div className="domain-token"><span className="at">@</span>shiftin.co</div>
            <div className="domain-help">Tout compte hors de ce domaine est rejeté à la connexion.</div>
          </div>
          <div className="domain-chk"><Check /> Vérifié</div>
        </div>
        <p className="srow-help" style={{ padding: '4px 6px 8px' }}>
          Les comptes et leurs droits se gèrent dans <b>Accès &amp; utilisateurs</b> — un compte invité
          se connecte avec Google sans intervention serveur. Les employés (Espace employé) sont admis
          via l’email de leur fiche.
        </p>
      </div>
    </section>
  );
}
